import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { NAV } from "@/constants/testIds";
import AssistantPanel from "@/components/AssistantPanel";
import {
  LayoutDashboard, Users2, Timer, ShieldCheck, BookOpen, Mail, Radio,
  Phone, FileText, ScrollText, UserCog, LogOut, Bot, Search, X, Activity,
  Cog, Zap, Tag, KeyRound, BarChart3, Plug, Flame, Gift,
} from "lucide-react";

const link =
  "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium " +
  "text-slate-600 transition-colors duration-150 hover:text-slate-900 hover:bg-slate-100";
const active = "text-slate-900 bg-orange-50 border border-orange-200";

function NavItem({ to, icon: Icon, label, testId, end = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) => `${link} ${isActive ? active : "border border-transparent"}`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>{label}</span>
    </NavLink>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ users: [], messages: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.length < 2) { setResults({ users: [], messages: [] }); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data);
      } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative w-full max-w-lg">
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <input
          data-testid={NAV.globalSearch}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search users, messages, phone, city…"
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
        />
        {q && (
          <button onClick={() => { setQ(""); setOpen(false); }} className="absolute right-2 top-2 text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && q.length >= 2 && (
        <div className="absolute top-11 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg z-40 max-h-[420px] overflow-y-auto">
          {loading && <div className="p-4 text-xs text-slate-500">Searching…</div>}
          {!loading && results.users.length === 0 && results.messages.length === 0 && (
            <div className="p-4 text-xs text-slate-500">No matches.</div>
          )}
          {results.users.length > 0 && (
            <div>
              <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">People</div>
              {results.users.map((u) => (
                <Link
                  key={u.id}
                  to={`/users/${u.id}`}
                  onClick={() => { setOpen(false); setQ(""); }}
                  className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors duration-150"
                >
                  <div>
                    <div className="text-sm font-semibold">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.email} · {u.exam} · {u.city}</div>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 uppercase font-semibold text-slate-600 tracking-widest">
                    {u.subscription_status}
                  </span>
                </Link>
              ))}
            </div>
          )}
          {results.messages.length > 0 && (
            <div className="border-t border-slate-100">
              <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Messages</div>
              {results.messages.map((m) => (
                <Link
                  key={m.id}
                  to={`/users/${m.trial_user_id}`}
                  onClick={() => { setOpen(false); setQ(""); }}
                  className="block px-3 py-2 hover:bg-slate-50 transition-colors duration-150"
                >
                  <div className="text-xs text-slate-500 tabular">
                    {new Date(m.created_at).toLocaleString()} · {m.channel} · {m.author}
                  </div>
                  <div className="text-sm truncate">{m.body}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const isAdmin = user?.role === "super_admin";

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-900 flex">
      {/* Sidebar */}
      <aside
        data-testid={NAV.sidebar}
        className="w-72 shrink-0 border-r border-slate-200 bg-white sticky top-0 h-screen flex flex-col"
      >
        <div className="p-4 border-b border-slate-200">
          <Link to="/" className="flex flex-col items-center gap-0 group">
            <img
              src="/brand/prephacker-logo.webp"
              alt="PrepHacker"
              className="h-40 w-auto object-contain mb-0"
            />
            <div className="text-sm font-bold uppercase tracking-[0.42em] text-slate-700 -mt-4">
              Connect
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <div className="px-1 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            Workspace
          </div>
          <NavItem to="/" icon={LayoutDashboard} label="Dashboard" testId={NAV.linkDashboard} end />
          <NavItem to="/users" icon={Users2} label="Users" testId={NAV.linkUsers} />
          <NavItem to="/statistics" icon={BarChart3} label="Statistics" testId={NAV.linkStatistics} />
          <NavItem to="/digests" icon={FileText} label="AI Digests" testId={NAV.linkDigests} />
          <NavItem to="/audit" icon={ScrollText} label="Audit Logs" testId={NAV.linkAudit} />
          <NavItem to="/communication-logs" icon={Activity} label="Communication Logs" testId="nav-link-comm-logs" />

          {isAdmin && (
            <>
              <div className="px-1 pt-4 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                AI Configuration
              </div>
              <NavItem to="/cadence" icon={Timer} label="Cadences" testId={NAV.linkCadence} />
              <NavItem to="/guardrails" icon={ShieldCheck} label="AI Guardrails" testId={NAV.linkGuardrails} />
              <NavItem to="/knowledge-base" icon={BookOpen} label="Knowledge Base" testId={NAV.linkKB} />
              <NavItem to="/templates" icon={Mail} label="Templates" testId={NAV.linkTemplates} />
              <NavItem to="/offers" icon={Gift} label="Offer Codes" testId="nav-link-offers" />
              <NavItem to="/promo-analytics" icon={BarChart3} label="Promo Analytics" testId="nav-link-promo-analytics" />
              <NavItem to="/groups" icon={Users2} label="Groups" testId="nav-link-groups" />
              <NavItem to="/labels" icon={Tag} label="Labels" testId={NAV.linkLabels} />
              <NavItem to="/whatsapp-numbers" icon={Phone} label="WhatsApp Numbers" testId={NAV.linkWaNumbers} />
              <NavItem to="/messaging-connectors" icon={Plug} label="Messaging Connectors" testId="nav-link-messaging-connectors" />
              <NavItem to="/ai-control" icon={Zap} label="AI Control Panel" testId={NAV.linkAIControl} />
              <NavItem to="/behavior-signals" icon={Radio} label="Behavior Signals" testId="nav-link-behavior-signals" />

              <div className="px-1 pt-4 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                Admin
              </div>
              <NavItem to="/team" icon={UserCog} label="Team" testId={NAV.linkTeam} />
              <NavItem to="/settings" icon={Cog} label="Settings" testId={NAV.linkSettings} />
            </>
          )}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div
            data-testid={NAV.userBadge}
            className="flex items-center justify-between gap-2 px-2 py-2 rounded-md bg-slate-50 border border-slate-200"
          >
            <div className="min-w-0">
              <div className="text-xs font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">
                {user?.role?.replace("_", " ")}
              </div>
            </div>
            <button
              onClick={() => setPwOpen(true)}
              data-testid="nav-change-password-btn"
              className="p-1.5 rounded-md text-slate-500 hover:bg-white hover:text-slate-900 transition-colors duration-150"
              title="Change password"
            >
              <KeyRound className="h-4 w-4" />
            </button>
            <button
              data-testid={NAV.logout}
              onClick={() => { logout(); navigate("/login"); }}
              className="p-1.5 rounded-md text-slate-500 hover:bg-white hover:text-slate-900 transition-colors duration-150"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Top bar with search */}
        <div className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200 px-6 lg:px-10 py-3 flex items-center gap-4">
          <GlobalSearch />
          <div className="flex-1" />
          <button
            data-testid={NAV.askConnectBtn}
            onClick={() => setAssistantOpen(true)}
            className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-slate-800 transition-colors duration-150"
          >
            <Bot className="h-3.5 w-3.5" /> Ask Pippy
          </button>
        </div>
        <Outlet />
      </main>

      <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!current || !next) return toast.error("All fields required");
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. Use the new one from next login.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Change failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-6"
        data-testid="change-password-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-orange-600" /> Change password
          </h3>
          <button type="button" onClick={onClose}><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Current password</label>
            <input
              type="password" autoComplete="current-password"
              data-testid="pw-current-input"
              value={current} onChange={(e) => setCurrent(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">New password</label>
            <input
              type="password" autoComplete="new-password"
              data-testid="pw-new-input"
              value={next} onChange={(e) => setNext(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
            />
            <div className="text-[11px] text-slate-500 mt-1">Minimum 6 characters.</div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Confirm new password</label>
            <input
              type="password" autoComplete="new-password"
              data-testid="pw-confirm-input"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            data-testid="pw-submit-btn"
            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-500 disabled:opacity-60"
          >
            {busy ? "Saving…" : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}
